import React, { ReactNode, useMemo, useState } from 'react'

import { UserAvatar } from '@digital-twins/design-system/components/ui/UserAvatar'
import { cn } from '@digital-twins/design-system/lib/utils'
import { QuoteListChip, QuoteListQuote } from '@digital-twins/slide-schemas/generated/plot-types'
import { StoredPlotPayload } from '../contract'
import { glossedQuote, glossedQuoteText } from './glossedQuote'

/**
 * The `quote-list` gallery layout (wave 7, Q16): the quick-study quotes view.
 *
 * Draws what Python stamped and decides nothing about a quote:
 * - the featured quote (`featuredQuoteId`, or the version's own from
 *   `featuredByVersion` while the gallery is filtered to one version; on a
 *   slice that lost it, the first quote still shown in `featuredOrder`) as a
 *   hero with a large portrait, then a grid of compact cards;
 * - the speaker as joined by Python (the anonymous label when no name was
 *   stamped, never an invented one), the portrait as stamped or as the host
 *   fills it by `respondentId`, the initials only when stamped;
 * - the typed chips in stamped order under the verbatim, the score as a badge.
 *
 * Interactive chrome (static renders and documents draw none of it): a version
 * filter when the quotes carry two or more versions, "Read more" on a long
 * verbatim, and per-version sections (`groupBy: asset`) that preview six quotes
 * behind "Load all". Words come from `data.labels` (Python, request language).
 * Never imports the frontend's `@/lib`.
 */

export type QuoteGalleryData = StoredPlotPayload<'quote-list'>['data']

export interface QuoteGalleryAvatarArgs {
  quote: QuoteListQuote
  name: string
  size: 'sm' | 'xl'
  /** What the gallery draws when the host has no image: the stamped initials, else the anonymous circle. */
  fallback: ReactNode
}

export interface QuoteGalleryProps {
  data: QuoteGalleryData
  groupBy?: 'none' | 'theme' | 'asset'
  renderMode?: 'interactive' | 'static'
  /** The host's portrait for a speaker with none stamped (keyed by `respondentId`). */
  imageOf?: (respondentId: string) => string | null | undefined
  /** The host draws the avatar (e.g. a thumbnail with a fallback); the gallery's own otherwise. */
  renderAvatar?: (args: QuoteGalleryAvatarArgs) => ReactNode
  /** The host renders a verbatim or a label (e.g. bolding comparison names); plain text otherwise. */
  renderText?: (text: string) => ReactNode
  /** Copy for the empty gallery, in the viewer's language (host words). */
  emptyLabel?: string | null
}

/** A verbatim past this many characters is clamped behind "Read more" (interactive only). */
export const QUOTE_GALLERY_CLAMP_CHARS = 220
/** A version section previews this many quotes behind "Load all" (interactive only). */
export const QUOTE_GALLERY_SECTION_PREVIEW = 6

function versionOf(quote: QuoteListQuote): QuoteListChip | null {
  return (quote.chips ?? []).find((chip) => chip.kind === 'version' && chip.id) ?? null
}

function scoreOf(quote: QuoteListQuote): string | null {
  return (quote.chips ?? []).find((chip) => chip.kind === 'score')?.label ?? null
}

function speakerName(quote: QuoteListQuote, anonymous: string): string {
  return quote.speaker?.name?.trim() || anonymous
}

function GalleryAvatar(props: {
  quote: QuoteListQuote
  name: string
  size: 'sm' | 'xl'
  imageOf?: QuoteGalleryProps['imageOf']
  renderAvatar?: QuoteGalleryProps['renderAvatar']
}) {
  const { quote, name, size, imageOf, renderAvatar } = props

  const [broken, setBroken] = useState(false)

  const speaker = quote.speaker
  const image = speaker?.imageUrl || (speaker?.respondentId && imageOf ? imageOf(speaker.respondentId) : null) || null
  const fallback = <UserAvatar initials={speaker?.initials ?? ''} name={name} size={size} />
  if (renderAvatar) {
    return <>{renderAvatar({ quote: image ? { ...quote, speaker: { ...speaker, imageUrl: image } } : quote, name, size, fallback })}</>
  }
  if (image && !broken) {
    return (
      <img
        src={image}
        alt={name}
        onError={() => setBroken(true)}
        data-testid="quote-gallery-portrait"
        className={cn(size === 'xl' ? 'h-20 w-20' : 'h-9 w-9', 'shrink-0 rounded-full border border-border object-cover')}
      />
    )
  }
  return fallback
}

function Chip(props: { chip: QuoteListChip; renderText: (text: string) => ReactNode }) {
  const { chip, renderText } = props

  return (
    <span
      className="inline-flex max-w-full items-center rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
      data-testid={`quote-gallery-chip-${chip.kind}`}
    >
      {renderText(chip.label)}
    </span>
  )
}

function ChipRow(props: { quote: QuoteListQuote; hideVersion: boolean; renderText: (text: string) => ReactNode }) {
  const { quote, hideVersion, renderText } = props

  const chips = (quote.chips ?? []).filter((chip) => chip.kind !== 'score' && !(hideVersion && chip.kind === 'version'))
  if (chips.length === 0) {
    return null
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="quote-gallery-chips">
      {chips.map((chip, index) => (
        <Chip key={`${chip.kind}-${index}`} chip={chip} renderText={renderText} />
      ))}
    </div>
  )
}

function ScoreBadge(props: { quote: QuoteListQuote }) {
  const score = scoreOf(props.quote)
  if (!score) {
    return null
  }
  return (
    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-foreground" data-testid="quote-gallery-score">
      {score}
    </span>
  )
}

interface CardProps {
  quote: QuoteListQuote
  data: QuoteGalleryData
  interactive: boolean
  hideVersion: boolean
  imageOf?: QuoteGalleryProps['imageOf']
  renderAvatar?: QuoteGalleryProps['renderAvatar']
  renderText: (text: string) => ReactNode
}

/** The verbatim: glossed from its stamped runs, else the host's rendering. */
function quoteBody(quote: QuoteListQuote, renderText: (text: string) => ReactNode): ReactNode {
  return quote.textRuns?.length ? glossedQuote(quote.text, quote.textRuns) : renderText(quote.text)
}

function FeaturedCard(props: CardProps) {
  const { quote, data, hideVersion, imageOf, renderAvatar, renderText } = props

  const name = speakerName(quote, data.labels.plot_quote_anonymous ?? '')
  return (
    <figure className="relative overflow-hidden rounded-2xl border border-border bg-muted/30 p-6 shadow-sm" data-testid="quote-gallery-featured" data-quote-id={quote.id}>
      <div className="flex items-center gap-4">
        <GalleryAvatar quote={quote} name={name} size="xl" imageOf={imageOf} renderAvatar={renderAvatar} />
        <figcaption className="min-w-0 flex-1">
          <p className="text-lg font-semibold text-foreground">{name}</p>
          {quote.speaker?.role ? <p className="text-sm text-muted-foreground">{quote.speaker.role}</p> : null}
        </figcaption>
        <ScoreBadge quote={quote} />
      </div>
      <blockquote className="mt-5 text-xl font-medium leading-relaxed text-foreground" data-testid="quote-gallery-text">
        “{quoteBody(quote, renderText)}”
      </blockquote>
      <ChipRow quote={quote} hideVersion={hideVersion} renderText={renderText} />
    </figure>
  )
}

function QuoteCard(props: CardProps) {
  const { quote, data, interactive, hideVersion, imageOf, renderAvatar, renderText } = props

  const [expanded, setExpanded] = useState(false)

  const name = speakerName(quote, data.labels.plot_quote_anonymous ?? '')
  const isLong = interactive && glossedQuoteText(quote.text, quote.textRuns).length > QUOTE_GALLERY_CLAMP_CHARS
  return (
    <figure className="flex h-full flex-col gap-3 rounded-xl border border-border bg-background p-4" data-testid="quote-gallery-card" data-quote-id={quote.id}>
      <div className="flex items-center gap-2.5">
        <GalleryAvatar quote={quote} name={name} size="sm" imageOf={imageOf} renderAvatar={renderAvatar} />
        <figcaption className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{name}</p>
          {quote.speaker?.role ? <p className="text-xs text-muted-foreground">{quote.speaker.role}</p> : null}
        </figcaption>
        <ScoreBadge quote={quote} />
      </div>
      <blockquote className={cn('text-sm leading-relaxed text-foreground', isLong && !expanded && 'line-clamp-6')} data-testid="quote-gallery-text">
        “{quoteBody(quote, renderText)}”
      </blockquote>
      <ChipRow quote={quote} hideVersion={hideVersion} renderText={renderText} />
      {isLong ? (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="mt-auto self-start text-xs font-medium text-foreground hover:underline"
          data-testid="quote-gallery-read-more"
        >
          {expanded ? data.labels.plot_show_less : data.labels.plot_read_more}
        </button>
      ) : null}
    </figure>
  )
}

export interface QuoteGalleryCardProps {
  quote: QuoteListQuote
  /** The stamped gallery the quote belongs to (its labels: the anonymous name, "Read more"). */
  data: QuoteGalleryData
  renderMode?: 'interactive' | 'static'
  imageOf?: QuoteGalleryProps['imageOf']
  renderAvatar?: QuoteGalleryProps['renderAvatar']
  renderText?: (text: string) => ReactNode
}

/**
 * One gallery card on its own (W7-T4b): a host that cites a single stamped quote (the
 * insight narrative's testimonial) draws the gallery's card, speaker and chips as stamped.
 */
export function QuoteGalleryCard(props: QuoteGalleryCardProps) {
  const { quote, data, renderMode = 'interactive', imageOf, renderAvatar, renderText = (text: string) => text } = props

  return (
    <QuoteCard
      quote={quote}
      data={data}
      interactive={renderMode !== 'static'}
      hideVersion={false}
      imageOf={imageOf}
      renderAvatar={renderAvatar}
      renderText={renderText}
    />
  )
}

function countLabel(data: QuoteGalleryData, count: number): string {
  const template = count === 1 ? data.labels.plot_one_quote : data.labels.plot_n_quotes
  return (template ?? '').replace('{n}', String(count))
}

function Section(props: {
  versionId: string
  label: string
  quotes: Array<QuoteListQuote>
  card: Omit<CardProps, 'quote'>
}) {
  const { versionId, label, quotes, card } = props

  const [expanded, setExpanded] = useState(false)

  const hasMore = card.interactive && quotes.length > QUOTE_GALLERY_SECTION_PREVIEW
  const visible = expanded || !hasMore ? quotes : quotes.slice(0, QUOTE_GALLERY_SECTION_PREVIEW)
  return (
    <div className="space-y-2" data-testid="quote-gallery-section" data-version-id={versionId}>
      <h3 className="text-base font-medium text-foreground" data-testid="quote-gallery-section-heading">
        {card.renderText(label)}
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {visible.map((quote) => (
          <QuoteCard key={quote.id} quote={quote} {...card} />
        ))}
      </div>
      {hasMore ? (
        <div className="flex justify-center pt-1">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-foreground"
            data-testid="quote-gallery-load-toggle"
          >
            {expanded ? card.data.labels.plot_show_less : card.data.labels.plot_load_all}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The hero among the quotes on show: the stamped featured quote when it is
 * shown, else the first shown quote in Python's ranking (`featuredOrder`), so
 * a slice (a segment cut, a version filter) with quotes always has a hero.
 * Nothing is ranked here; with no stamp there is no hero.
 */
export function pickFeatured(
  shown: Array<QuoteListQuote>,
  featuredId: string | null,
  order?: Array<string> | null,
): QuoteListQuote | null {
  const byId = new Map(shown.map((quote) => [quote.id, quote]))
  if (featuredId && byId.has(featuredId)) {
    return byId.get(featuredId) ?? null
  }
  const id = (order ?? []).find((entry) => byId.has(entry))
  return id ? byId.get(id) ?? null : null
}

export function QuoteGallery(props: QuoteGalleryProps) {
  const { data, groupBy = 'none', renderMode = 'interactive', imageOf, renderAvatar, renderText = (text: string) => text, emptyLabel } = props

  const [filter, setFilter] = useState<string | null>(null)

  const interactive = renderMode !== 'static'
  const versions = useMemo(() => {
    const seen: Array<QuoteListChip> = []
    for (const quote of data.quotes) {
      const version = versionOf(quote)
      if (version && !seen.some((entry) => entry.id === version.id)) {
        seen.push(version)
      }
    }
    return seen
  }, [data.quotes])

  if (data.quotes.length === 0) {
    return emptyLabel ? (
      <p className="px-1 py-6 text-center text-sm text-muted-foreground" data-testid="quote-gallery-empty">
        {emptyLabel}
      </p>
    ) : null
  }

  const sectioned = groupBy === 'asset' && versions.length > 1
  const canFilter = interactive && !sectioned && versions.length >= 2
  const active = canFilter && filter && versions.some((version) => version.id === filter) ? filter : null
  const shown = active ? data.quotes.filter((quote) => versionOf(quote)?.id === active) : data.quotes
  const featuredId = active
    ? data.featuredByVersion?.find((entry) => entry.versionId === active)?.quoteId ?? null
    : data.featuredQuoteId ?? null
  const featured = pickFeatured(shown, featuredId, data.featuredOrder)
  const rest = shown.filter((quote) => quote !== featured)
  const card = { data, interactive, imageOf, renderAvatar, renderText }

  return (
    <div className="space-y-4" data-testid="quote-gallery">
      <span className="block text-xs tabular-nums text-muted-foreground" data-testid="quote-gallery-count">
        {countLabel(data, shown.length)}
      </span>
      {canFilter ? (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="quote-gallery-filter">
          {[{ id: null as string | null, label: data.labels.plot_filter_all ?? '', count: data.quotes.length }, ...versions.map((version) => ({ id: version.id ?? null, label: version.label, count: data.quotes.filter((quote) => versionOf(quote)?.id === version.id).length }))].map((option) => (
            <button
              key={option.id ?? 'all'}
              type="button"
              onClick={() => setFilter(option.id === active ? null : option.id)}
              aria-pressed={option.id === active}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition-colors',
                option.id === active ? 'border-transparent bg-foreground text-background' : 'border-border text-muted-foreground hover:text-foreground',
              )}
              data-testid={option.id === null ? 'quote-gallery-filter-all' : 'quote-gallery-filter-option'}
            >
              {option.label} · {option.count}
            </button>
          ))}
        </div>
      ) : null}
      {featured ? <FeaturedCard quote={featured} {...card} hideVersion={false} /> : null}
      {sectioned ? (
        <>
          {versions.map((version) => {
            const quotes = rest.filter((quote) => versionOf(quote)?.id === version.id)
            return quotes.length > 0 ? (
              <Section key={version.id} versionId={version.id ?? ''} label={version.label} quotes={quotes} card={{ ...card, hideVersion: true }} />
            ) : null
          })}
          {/* Quotes with no version chip still draw (after the sections), so the count
              always matches what is drawn. */}
          {rest.some((quote) => !versionOf(quote)) ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="quote-gallery-unversioned">
              {rest.filter((quote) => !versionOf(quote)).map((quote) => (
                <QuoteCard key={quote.id} quote={quote} {...card} hideVersion={false} />
              ))}
            </div>
          ) : null}
        </>
      ) : rest.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="quote-gallery-grid">
          {rest.map((quote) => (
            <QuoteCard key={quote.id} quote={quote} {...card} hideVersion={false} />
          ))}
        </div>
      ) : null}
    </div>
  )
}
